import { useBooleanChoices } from 'hooks/useBooleanChoices';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import {
  FilterAutocompleteChip,
  FilterMultiselectChip,
  FilterTextChip,
} from 'shared/mui/Filter/FilterChipsToolbar/FilterChips';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';

export const GatewayRulesFiltersChips: React.FC = () => {
  const translate = useTypedTranslate();
  const booleanChoices = useBooleanChoices({ useNumericKeys: true });
  const { resetField, setValue } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();

  return (
    <>
      <FilterTextChip label={translate('app.filter.name')} name="name">
        <TextInput
          helperText={false}
          label={translate('app.filter.name')}
          name="name"
          resettable
          source="name"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip label={translate('app.filter.ruleId')} name="id">
        <TextInput
          alwaysOn
          helperText={false}
          key="id"
          label={translate('app.filter.ruleId')}
          resettable
          source="id"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip
        getName={(filterValue: string) =>
          booleanChoices.find((v) => filterValue === v.id)?.name || ''
        }
        label={translate('app.filter.active')}
        name="active"
      >
        <AutocompleteInput
          choices={booleanChoices}
          helperText={false}
          label={translate('app.filter.active')}
          name="active"
          optionText="name"
          source="active"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterAutocompleteChip
        label={translate('app.filter.merchant')}
        name="merchant"
        onApply={() => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('method', null);
        }}
        onDelete={() => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('method', null);
        }}
        onReset={() => {
          setValue('provider', appliedFilters?.provider);
          setValue('gateway', appliedFilters?.gateway);
          setValue('method', appliedFilters?.method);

          setAppliedFilter('provider', appliedFilters?.provider);
          setAppliedFilter('gateway', appliedFilters?.gateway);
          setAppliedFilter('method', appliedFilters?.method);
        }}
        resource="merchants"
      >
        <ReferenceInput name="merchant" perPage={100} reference="merchants" source="merchant">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.merchant')}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>

      <FilterAutocompleteChip
        label={translate('app.filter.provider')}
        name="provider"
        onApply={() => {
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('gateway', []);
          setAppliedFilter('method', null);
        }}
        onDelete={() => {
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('gateway', []);
          setAppliedFilter('method', null);
        }}
        onReset={() => {
          setValue('gateway', appliedFilters?.gateway);
          setValue('method', appliedFilters?.method);

          setAppliedFilter('gateway', appliedFilters?.gateway);
          setAppliedFilter('method', appliedFilters?.method);
        }}
        resource="providers"
      >
        <ReferenceInput
          name="provider"
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.provider')}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>
      <FilterMultiselectChip
        label={translate('app.filter.gateways')}
        limitTags={1}
        name="gateway"
        onApply={() => {
          resetField('method', { keepDirty: true });
          setAppliedFilter('method', null);
        }}
        onDelete={() => {
          resetField('method', { keepDirty: true });
          setAppliedFilter('method', null);
        }}
        onReset={() => {
          setValue('method', appliedFilters?.method);
          setAppliedFilter('method', appliedFilters?.method);
        }}
        resource="gateways"
      >
        <ReferenceArrayInput key="gateway" perPage={100} reference="gateways" source="gateway">
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.gateways')}
            name="gateway"
            optionText="name"
          />
        </ReferenceArrayInput>
      </FilterMultiselectChip>
      <FilterAutocompleteChip
        label={translate('app.filter.method')}
        name="method"
        resource="methods"
      >
        <ReferenceInput name="method" perPage={100} reference="methods" source="method">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.method')}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>
      <FilterTextChip
        label={translate('app.merchantAccounts.subagentId')}
        name="mappings.mappingItems.subagent"
      >
        <TextInput
          alwaysOn
          helperText={false}
          key="subagent-id"
          label={translate('app.merchantAccounts.subagentId')}
          resettable
          source="mappings.mappingItems.subagent"
          sx={{ minWidth: 0 }}
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip
        label={translate('app.filter.clientId')}
        name="conditionGroup.expressions.clientId"
      >
        <TextInput
          helperText={false}
          label={translate('app.filter.clientId')}
          name="conditionGroup.expressions.clientId"
          resettable
          source="conditionGroup.expressions.clientId"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterMultiselectChip
        label={translate('app.filter.countries')}
        name="country"
        resource="countries"
      >
        <ReferenceArrayInput perPage={100} reference="countries" source="country">
          <AutocompleteMultiselectInput
            helperText={false}
            label={translate('app.filter.countries')}
            name="country"
            optionText="name"
            size="small"
          />
        </ReferenceArrayInput>
      </FilterMultiselectChip>
      <FilterMultiselectChip
        label={translate('app.filter.currencies')}
        name="currency"
        resource="currencies"
      >
        <ReferenceArrayInput
          filter={{ active: true }}
          perPage={1000}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            getOptionLabel={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
            helperText={false}
            label={translate('app.filter.currencies')}
            name="currency"
            size="small"
          />
        </ReferenceArrayInput>
      </FilterMultiselectChip>
    </>
  );
};
